import React, { useState, useEffect } from 'react'
import {
	getUTCEndDate,
	getUTCStartDate,
	getFEFormattedDate,
} from '../../widgets/CustomDateRangePicker/CustomDateRangePicker.utils'
import { useDispatch, useSelector } from 'react-redux'
import {
	setReportFilters,
	resetChurnedCustomers,
	resetUnengagedUsers,
	setIsDateSelected,
} from './reports.slice'
import {
	selectFilters,
	selectReportCustomers,
	selectStores,
	selectPreviousFilters,
} from './reports.selectors'
import { selectAccountUsers } from '../Account/account.selector'
import { selectIsAdmin } from '../Profile/profile.selectors'
import { USER_ROLES_ENUMS } from '../Account/account.constants'
import {
	DATE_RANGE_FILTERS,
	getFormattedTitle,
	getFormattedAccountName,
} from './reports.constants'
import { getDateRangeByDateFilter } from '../../helpers/getDateRangeByDateFilter'
import { isEqual } from 'lodash'
import DashboardCard from '../../components/cards/DashboardCard'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

const CUSTOM_DATE_RANGE = 'Custom Date Range'
const CURRENT_MONTH = 'This Month So Far'

const ReportsFilter = () => {
	const [dateRange, setDateRange] = useState()
	const [selectedOptions, setSelectedOptions] = useState([])
	const dispatch = useDispatch()
	const filters = useSelector(selectFilters)
	const previousFilters = useSelector(selectPreviousFilters)
	const stores = useSelector(selectStores)
	const accountUsers = useSelector(selectAccountUsers)
	const isAdmin = useSelector(selectIsAdmin)
	const { data } = useSelector(selectReportCustomers)
	const [selectedDateFilter, setSelectedDateFilter] = useState('')
	const isFilterChange = !isEqual(filters, previousFilters)

	const salesRepresentatives = accountUsers.filter(
		(each) => each.userRole === USER_ROLES_ENUMS.SALES
	)

	useEffect(() => {
		if (isFilterChange) {
			dispatch(resetUnengagedUsers())
			dispatch(resetChurnedCustomers())
		}
	}, [filters, dispatch, isFilterChange])

	const handleSelectedOptions = (options) => {
		setSelectedOptions(options)
		if (options.length) {
			const selectedId = options.map((each) => each.id)
			dispatch(
				setReportFilters({
					...filters,
					store: selectedId,
					sales: '',
				})
			)
		} else
			dispatch(
				setReportFilters({
					...filters,
					store: '',
					sales: '',
				})
			)
	}

	const onClose = (startDate, endDate) => {
		if (startDate && endDate) {
			setSelectedDateFilter(
				`${getFEFormattedDate(startDate)} - ${getFEFormattedDate(endDate)}`
			)
			dispatch(
				setReportFilters({
					...filters,
					startDate: getUTCStartDate(startDate),
					endDate: getUTCEndDate(endDate),
				})
			)
		}
	}

	const handleSingleSelection = (value) => {
		setSelectedDateFilter(value)

		if (value !== CUSTOM_DATE_RANGE) {
			dispatch(setIsDateSelected(false))
			const [startDate, endDate] = getDateRangeByDateFilter(value)
			startDate &&
				endDate &&
				dispatch(
					setReportFilters({
						...filters,
						startDate,
						endDate,
					})
				)
		} else {
			dispatch(setIsDateSelected(true))
		}
	}

	return (
		<DashboardCard>
			<div className='flex flex-row gap-2 items-center mt-6'>
				<div className='w-full'>
					<Select
						value={filters.customer ?? ''}
						onValueChange={(val) => {
							dispatch(
								setReportFilters({
									...filters,
									customer: val,
									sales: '',
									store: '',
								})
							)
							setSelectedOptions([])
						}}
					>
						<SelectTrigger className='w-full'>
							<SelectValue placeholder='All Customers' />
						</SelectTrigger>
						<SelectContent>
							{data &&
								data.map((each) => (
									<SelectItem key={each.id} value={each.id} title={getFormattedTitle(each.account)}>
										{getFormattedAccountName(each.id, each)}
									</SelectItem>
								))}
						</SelectContent>
					</Select>
				</div>
				{filters.customer && (
					<div className='w-full'>
						<Select
							value={Array.isArray(filters.store) ? (filters.store[0] ?? '') : (filters.store ?? '')}
							onValueChange={(val) => {
								dispatch(
									setReportFilters({
										...filters,
										store: val,
										sales: '',
									})
								)
								setSelectedOptions([])
							}}
						>
							<SelectTrigger className='w-full'>
								<SelectValue placeholder='Select FFL' />
							</SelectTrigger>
							<SelectContent>
								{stores && stores.map((each) => (
									<SelectItem key={each.id} value={each.id}>
										{each.fflName || each.name || `FFL ${each.id}`}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
				<div className='w-full'>
					<Select
						value={selectedDateFilter || CURRENT_MONTH}
						onValueChange={(val) => handleSingleSelection(val)}
					>
						<SelectTrigger className='w-full'>
							<SelectValue placeholder={CURRENT_MONTH} />
						</SelectTrigger>
						<SelectContent>
							{DATE_RANGE_FILTERS.map((opt) => (
								<SelectItem key={opt.id} value={opt.value}>
									{opt.value}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className='w-full'>
					{isAdmin && (
						<Select
							value={filters.sales ?? ''}
							onValueChange={(val) => {
								dispatch(
									setReportFilters({
										...filters,
										customer: '',
										store: '',
										sales: val,
									})
								)
							}}
						>
							<SelectTrigger className='w-full'>
								<SelectValue placeholder='All Sales Representatives' />
							</SelectTrigger>
							<SelectContent>
								{salesRepresentatives.map((each) => (
									<SelectItem key={each.id} value={each.id}>
										{each.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				</div>
			</div>
		</DashboardCard>
	)
}

export default ReportsFilter
